import {
  Button,
  Card,
  Col,
  Flex,
  Input,
  Row,
  Select,
  Table,
  Typography,
  Tag,
  Avatar
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routes, userRoles } from "../../../../../../constants";
import Actions from "../../../../../../redux/actions";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate, getUserStatus } from "../../../../../../utils/utils";
import { getCustomersAction } from "../../../../../../redux/services/customers/customersServices";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { colors } from "../../../../../../constants/colors";
import { CustomerCountIcon } from "../../../../../../assets/svg";
import { SearchOutlined } from "@ant-design/icons";
import { getServiceProviderCustomersAction } from "../../../../../../redux/services/service-provider-customers/serviceProviderCustomersServices";
import useDebounce from "../../../../../../hooks/useDebounce";
import { getCountryAction } from "../../../../../../redux/services/auth/authServices";
import { UserOutlined } from "@ant-design/icons";
import styles from "./CustomerInfo.module.scss";
import { baseRouteMaker } from "../../../../../../constants/routeConsts";

const CustomerInfo = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { serviceProviderCustomers } = useSelector(
    (state) => state.serviceProviderCustomers
  );
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: serviceProviderCustomers?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const { countryList } = useSelector((state) => state?.auth);
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  /* filter dependencies */

  const selectListApiCalls = () => {
    // Country select list
    dispatch(getCountryAction());
  };

  /* on Country changed */
  const onCountryChanged = (value, element) => {
    const selectedCountryObject = countryList?.find(
      (item) => item._id === value
    );
    setSelectedCountryObj(selectedCountryObject);
  };
  /* on Country changed */

  /* function definition to get table data */
  const handleGetTableData = () => {
    dispatch(
      Actions.createAction(Actions.GET_SERVICE_PROVIDER_CUSTOMERS_LIST, {
        serviceProviderCustomers: {},
      })
    );
    setLoading(true);
    const selCountryIdArr = new Array(selectedCountryObj?._id);

    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      country: selCountryIdArr,
    };
    dispatch(getServiceProviderCustomersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue, selectedCountryObj]);

  /* initial dependencies rendering */
  useEffect(() => {
    selectListApiCalls(null);
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.customer.customerId"),
        dataIndex: "id_number",
        key: "id_number",
      },
      // {
      //   title: "Date of Joining",
      //   dataIndex: "created_at",
      //   key: "created_at",
      //   render: (_, record) => (
      //     <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
      //   ),
      // },
      {
        title: t("Modules.customer.customerName"),
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                src={record?.profile_image}
                key={record?.profile_image}
                icon={<UserOutlined />}
              />
               {
                record?.first_name
            ? `${record?.first_name} ${record?.last_name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("staffList.email"),
        dataIndex: "email",
        key: "email",
      },
      {
        title: t("staffList.phone"),
        dataIndex: "phone",
        key: "phone",
        render: (phone) => `+${phone?.country_code}-${phone?.phone_number}`,
      },
      {
        title: t("CommonUtils.gender"),
        dataIndex: "gender",
        key: "gender",
        render: (_, record) => {
          return record.gender ? record.gender :' - '
        }
      },
      {
        title: t("staffList.availability"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getUserStatus(record?.status),
                  textTransform: "capitalize",
                }}
              >
                 {record?.status}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("Modules.customer.customerInfo"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                {t("viewText")}
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_CUSTOMER_ID, {
        selectedServiceProviderCustomerId: "",
      })
    );
    navigate(baseRouteMaker(routes.SERVICE_PROVIDER_CUSTOMER_INFO_DETAILS_DETAILS));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceProviderCustomerId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_SERVICE_PROVIDER_CUSTOMER_ID, {
          selectedServiceProviderCustomerId,
        })
      );
    }
  };

  return (
    <div className="serviceProviderCustomersInfoContainer">
      <Flex vertical gap="2rem">
        <Row gutter={24} justify="space-between">
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("Modules.customer.search")}
              onChange={(event) => setSearchValue(event.target.value)}
              style={{ width: "370px" }}
            />
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Select
              style={{ width: "370px" }}
              placeholder={t("ProfileSetup.countryPlaceholder")}
              onChange={(value, element) => onCountryChanged(value, element)}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {countryList &&
                countryList?.map((item) => (
                  <Select.Option key={item?._id} value={item?._id}>
                  {item?.name}
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col span={8}>
            <Card
              bordered
              hoverable
              style={{ 
                width: 240, padding: 0, borderRadius: 20,
                border: `1px solid ${colors.oceanBlue}`,
              }}
            >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={8}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<UserOutlined style={{ fontSize: 22 }} />}
                  />
                </Col>
                <Col span={16}>
                  <Flex vertical>
                    <Typography.Title level={4}>
                      {t("Modules.customer.customerCount")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.electricBlue }}
                    >
                      {serviceProviderCustomers?.total || 0}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={serviceProviderCustomers?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default CustomerInfo;
